import { iSharedCartPayload } from "../../api/sharedCart/sharedCart";
import { supplyTypeField } from "../booking/supplyType";

/**
 * Maps the `/search/shared-details` response into the booking-flow state the
 * review page consumes, so a shared cart renders through the EXISTING review UI.
 *
 * The response's `flight.details` is the *detailed* shape (legs carry `segments`),
 * but `FlightListingComponent` reads summary fields (`departureDate`/`arrivalDate`/
 * `airlineImage`) at the leg top level — the *search-result sector* shape. So we
 * lift those from each leg's first/last segment and keep the detailed legs under
 * `flightDetails` (used by the expanded card). `data.priceReview` is used as-is
 * for `promoReviewCartData` (no separate /price/review call for shared opens).
 * `data.activities[]` maps to `activities` (display-only card on the review page).
 */

// Lift summary fields from a leg's segments → matches the search-result sector
// leg shape that FlightListingComponent renders.
function toSummaryLeg(leg: any) {
  if (!leg) return leg;
  const segs = Array.isArray(leg.segments) ? leg.segments : [];
  const first = segs[0] || {};
  const last = segs[segs.length - 1] || {};
  return {
    ...leg,
    departureDate: leg.departureDate ?? first.departureDate,
    arrivalDate: leg.arrivalDate ?? last.arrivalDate,
    airlineImage: leg.airlineImage ?? first.marketingAirlineImage ?? first.operatingAirlineImage,
    // stopsCount / totalTime / route are already present on the leg
  };
}

// One shared activity, as rendered by SharedActivityReview on the review page.
// Prices come from the shared-details response; startTime / productOptionCode /
// uniqueId come from the stored snapshot (the response doesn't return them).
export interface iSharedCartActivity {
  activityName: string;
  productCode: string;
  baseFare: number;
  tax: number;
  totalFare: number;
  discount: number;
  currency: string;
  startTime: string;
  productOptionCode: string;
  uniqueId: string;
}

export interface iMappedSharedCart {
  promoReviewCartData: any;
  paymentId: string | null;
  selectedFlightOptions: { flights: boolean; hotels: boolean; activities: boolean };
  flightData: any | null;
  flightAvailabilityData: any | null;
  bookingPatch: Record<string, any>;
  selectedHotelRoomArr: any[];
  hotelData: any | null;
  hasHotels: boolean;
  activities: iSharedCartActivity[];
}

export function mapSharedCartDetails(data: any, snapshot: iSharedCartPayload): iMappedSharedCart {
  const hasFlight = !!data?.flight?.details;
  const hotelsArr = Array.isArray(data?.hotels) ? data.hotels : [];
  const hasHotels = hotelsArr.length > 0;

  // Authoritative paymentId from the shared-details response. The prebook
  // (`/booking`) reads it from `promoReviewCartData.price.paymentId`, so stamp
  // the top-level `data.paymentId` there — for shared opens this is the only
  // source (we don't call /price/review).
  const paymentId = data?.paymentId ?? data?.priceReview?.price?.paymentId ?? data?.priceReview?.paymentId ?? null;
  const promoReviewCartData = data?.priceReview
    ? { ...data.priceReview, price: { ...(data.priceReview.price || {}), paymentId } }
    : null;

  // Activities: prices from the response, ids/startTime from the snapshot
  // (matched by index — the snapshot has no productCode to match on).
  const activitiesArr = Array.isArray(data?.activities) ? data.activities : [];
  const snapActivities: any[] = Array.isArray(snapshot?.activity) ? (snapshot.activity as any[]) : [];
  const activities: iSharedCartActivity[] = activitiesArr.map((a: any, idx: number) => {
    const snapActivity = snapActivities[idx] || {};
    return {
      activityName: a?.activityName ?? "",
      productCode: a?.productCode ?? "",
      baseFare: a?.base_fare ?? 0,
      tax: a?.tax ?? 0,
      totalFare: a?.total_fare ?? 0,
      discount: a?.discount ?? 0,
      currency: a?.currency ?? "",
      startTime: snapActivity?.startTime ?? "",
      productOptionCode: snapActivity?.productOptionCode ?? "",
      uniqueId: snapActivity?.uniqueId ?? "",
    };
  });

  const result: iMappedSharedCart = {
    promoReviewCartData,
    paymentId,
    selectedFlightOptions: {
      flights: hasFlight,
      hotels: hasHotels,
      // Deliberately false even when the cart has activities: `activities: true`
      // makes ContentContainer swap the whole review page for ActivityBookingFlow,
      // which has no data here and opens on its blank search form. Shared
      // activities render on the ReviewNew page from `activities` below instead.
      activities: false,
    },
    flightData: null,
    flightAvailabilityData: null,
    bookingPatch: {},
    selectedHotelRoomArr: [],
    hotelData: null,
    hasHotels,
    activities,
  };

  if (hasFlight) {
    const fd = data.flight.details;
    const uniqueId: string = fd.uniqueId;
    // `flight.id` was stored as `${encryptedKey}-${uniqueId}`; recover encryptedKey.
    const storedId: string = (snapshot?.flight?.id as string) || "";
    const encryptedKey =
      uniqueId && storedId.endsWith(`-${uniqueId}`)
        ? storedId.slice(0, storedId.length - (uniqueId.length + 1))
        : storedId;
    const fareId = fd?.price?.fareId || snapshot?.flight?.fareId || "";
    const totalPrice =
      data?.priceReview?.fareDetails?.flight?.total_fare ??
      data?.flight?.fareBreakdown?.total ??
      fd?.price?.totalFare ??
      0;

    const outboundDetailed = Array.isArray(fd.outbound) ? fd.outbound : [];
    const inboundDetailed = Array.isArray(fd.inbound) ? fd.inbound : [];

    const sector = {
      ...fd,
      uniqueId,
      fareId,
      price: totalPrice,
      outbound: outboundDetailed.map(toSummaryLeg),
      inbound: inboundDetailed.map(toSummaryLeg),
      flightDetails: { outbound: outboundDetailed, inbound: inboundDetailed },
      isDocsMandatory: data?.flight?.isDocsMandatory ?? false,
      extraServiceDetails: data?.flight?.extraServiceDetails ?? {},
    };

    result.flightData = { encryptedKey, sectors: [sector] };
    result.flightAvailabilityData = {
      ...fd,
      uniqueId,
      encryptedKey,
      fareId,
      flightPrice: totalPrice,
      isDocsMandatory: data?.flight?.isDocsMandatory ?? false,
      extraServiceDetails: data?.flight?.extraServiceDetails ?? {},
      // The sharer's search supplyType, from the stored snapshot (shared-details
      // doesn't return it) — echoed as the recipient's prebook flightSupplyType.
      ...supplyTypeField(snapshot?.supplyTypes?.flight),
    };

    // City header (getCityPathString reads departureAddress/arrivalAddress).
    const firstSeg = outboundDetailed[0]?.segments?.[0];
    const lastOutLeg = outboundDetailed[outboundDetailed.length - 1];
    const lastSeg = lastOutLeg?.segments?.[lastOutLeg.segments.length - 1];
    const airPort = firstSeg
      ? [
          {
            departureAddress: firstSeg.departureCityName || firstSeg.depAirportCode || "",
            arrivalAddress: lastSeg?.arrivalCityName || lastSeg?.arrAirportCode || "",
          },
        ]
      : [];

    result.bookingPatch = {
      selectedFlightId: uniqueId,
      isFlightAvailable: true,
      isFlightAvailableForPrebooking: false,
      selectedFlightDetails: [...outboundDetailed, ...inboundDetailed],
      tripType: fd.tripType ?? (inboundDetailed.length ? 2 : 1),
      airPort,
      adultsCount: fd.adults ?? 1,
      childrenCount: fd.children ?? 0,
      infantCount: fd.infants ?? 0,
    };
  }

  // Hotels: HotelReview renders from `hotelData.data[]` (city groups, each with
  // `hotels[]`) aligned BY INDEX with `selectedHotelRoomArr[]` (matched by
  // city + checkIn + checkOut + selectedHotelId). Build both in the same order.
  if (hasHotels) {
    const cityGroups: any[] = [];
    const rooms: any[] = [];
    // preBookId/sessionId aren't in the shared-details response — they were
    // persisted in the stored payload at share time. Look them up per hotel.
    const snapHotels: any[] = Array.isArray(snapshot?.hotels) ? (snapshot.hotels as any[]) : [];
    // Index-aligned with snapshot.hotels (see useShareCart).
    const snapHotelSupplyTypes: any[] = Array.isArray(snapshot?.supplyTypes?.hotels)
      ? (snapshot.supplyTypes?.hotels as any[])
      : [];
    hotelsArr.forEach((h: any, idx: number) => {
      const snapHotel = snapHotels.find((sh: any) => sh?.hotelId === h.hotelId) || snapHotels[idx] || {};
      // indexOf → -1 for the `{}` fallback, which reads as no supplyType.
      const snapHotelSupplyType = snapHotelSupplyTypes[snapHotels.indexOf(snapHotel)];
      const hd = h?.hotelDetails?.data || {};
      const room = Array.isArray(h?.roomDetails) ? h.roomDetails[0] : null;
      const roomPrice = room?.roomPrices?.[0] || {};
      // `selectedHotelRoom.bedInformation` is rendered as text (typed string),
      // but shared-details returns an array of {bedType, quantity}. Stringify
      // it ("2 Twin bed, 1 King bed") so React doesn't try to render objects.
      const bedInformation = Array.isArray(room?.bedInformation)
        ? room.bedInformation
            .map((b: any) => `${b?.quantity ?? ""} ${b?.bedType ?? ""}`.trim())
            .filter(Boolean)
            .join(", ")
        : room?.bedInformation ?? "";

      cityGroups.push({
        city: h.city,
        checkIn: h.checkIn,
        checkOut: h.checkOut,
        hotels: [
          {
            hotelId: h.hotelId,
            unicaId: h.unicaId,
            name: hd.name ?? h.hotelName ?? "",
            address: hd.address ?? "",
            stars: hd.stars ?? hd.starRating ?? 0,
            rating: hd.rating ?? 0,
            reviewCount: hd.reviewCount ?? 0,
            reviewHeading: hd.reviewHeading ?? "",
            hotelMainImage: hd.hotelMainImage ?? "",
            amenities: hd.hotelAmenities ?? [],
            // HotelReview reads `totalPrice.price` + `totalPrice.taxesIncluded`.
            totalPrice: { price: h.totalPrice ?? 0, taxesIncluded: roomPrice?.feesIncluded ?? true },
            cancellationPolicy: h.cancellationPolicy ?? "",
            hotelDetails: hd,
          },
        ],
      });

      rooms.push({
        city: h.city,
        checkIn: h.checkIn,
        checkOut: h.checkOut,
        selectedHotelRoom: {
          selectedHotelId: h.hotelId,
          selectedRoomId: roomPrice?.uniqueId ?? h.uniqueId ?? "NA",
          checkIn: h.checkIn,
          checkOut: h.checkOut,
          responseUniqueId: h.uniqueId ?? "NA",
          unicaId: h.unicaId ?? "",
          // Reused by the prebook — sourced from the stored snapshot, not the
          // shared-details response (which omits these).
          preBookId: snapHotel?.preBookId || "NA",
          sessionId: snapHotel?.sessionId || "NA",
          roomPricingSessionId: snapHotel?.roomPricingSessionId || snapHotel?.sessionId || "NA",
          // Echoed as the recipient's prebook hotel[].supplyType.
          ...supplyTypeField(snapHotelSupplyType),
          supplier: snapHotel?.supplier || roomPrice?.supplier || "",
          rateHotelId: snapHotel?.rateHotelId || roomPrice?.hotelId || "",
          type: roomPrice?.rateType ?? "",
          boardName: roomPrice?.boardName ?? "",
          excludedAmount: roomPrice?.excluded?.amount ?? 0,
          price: h.totalPrice ?? 0,
          actualPrice: h.baseFare ?? h.totalPrice ?? 0,
          feesIncluded: roomPrice?.feesIncluded ?? true,
          taxIncluded: roomPrice?.taxIncluded ?? "yes",
          isBooked: false,
          roomName: room?.roomName ?? "",
          bedInformation,
          roomImages: room?.roomImages ?? [],
          // Drives the "Cancellation Policy" link in HotelReview.
          discountValidity: h.cancellationPolicy ?? "",
          priceChanged: h.priceChanged ?? false,
          availabilityChecked: false,
        },
      });
    });
    result.selectedHotelRoomArr = rooms;
    // Search-level value too, when every shared room agrees — the "Select rooms" re-pick
    // and the room factory read hotelData.supplyType, which shared-details doesn't return.
    const sharedHotelSupplyTypes = rooms.map((r: any) => r?.selectedHotelRoom?.supplyType);
    const agreedHotelSupplyType =
      sharedHotelSupplyTypes.length > 0 && sharedHotelSupplyTypes.every((v: any) => v && v === sharedHotelSupplyTypes[0])
        ? sharedHotelSupplyTypes[0]
        : undefined;
    result.hotelData = { data: cityGroups, ...supplyTypeField(agreedHotelSupplyType) };
  }

  return result;
}
